import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  type NomenclatureDto,
} from '@eombor/contracts';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Skeleton,
  useToast,
  type Column,
} from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { ApiErrorState } from '../../../shared/ui/ApiErrorState.js';
import { ResponsiveTable } from '../../../shared/ui/ResponsiveTable.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { catalogApi } from '../api/catalog.api.js';
import { catalogKeys, useCategories, useNomenclatures } from '../model/catalog.queries.js';
import { NomenclatureDialog, type NomenclatureFormValues } from './NomenclatureDialog.js';
import { unitText } from '../../../shared/i18n/labels.js';

/** `''` — "kategoriyasiz"; backend `null` kutadi. */
const categoryIdOf = (raw: string): string | null => (raw === '' ? null : raw);

/**
 * Nomenklatura katalogi.
 *
 * Qidiruv `⌘K` **emas**, lokal maydon: buyruq paneli bo'limlar orasida
 * yuradi, bu yerda esa odam bitta ro'yxat ichida qoladi va har harfda
 * jadval qayta chiziladi.
 */
export const CatalogPage = () => {
  const { canAnywhere } = usePermission();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [editing, setEditing] = useState<NomenclatureDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /* O'chirish tasdig'i alohida holatda: qator bosilganda tahrir ochiladi va
     ikkalasi bitta oynani baham ko'rsa, "saqlash" bilan "o'chirish" yonma-yon
     turib qolardi. */
  const [deleting, setDeleting] = useState<NomenclatureDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * `canAnywhere`, `can` EMAS — va bu farq ekranda ko'rinadigan xatoni
   * tuzatadi.
   *
   * Katalog markaziy ma'lumotnoma: satrlarda `mtuId` yo'q va "boshqa MTU
   * ning nomenklaturasi" degan tushuncha umuman yo'q
   * (`catalog/domain/nomenclature.ts`). Ya'ni tekshiriladigan MAQSAD ham
   * yo'q — qaysi ombor yoki korxona haqida gap ketayotgani savolning o'zida
   * turmaydi.
   *
   * `can` esa maqsadsiz chaqirilganda ataylab `false` qaytaradi ("fail
   * closed"). Natijada doiraga biriktirilgan xodim — masalan ombor
   * doirasidagi skladchi — `catalog.manage` ruxsati BO'LA TURIB ekranda
   * hech qanday tugma ko'rmasdi, backend esa o'sha so'rovni bemalol qabul
   * qilardi. Tugmalar faqat superadminga chiqardi.
   *
   * Backend shu yerda aynan `canAnywhere` bilan bir xil ish qiladi:
   * `scopeFor(user, 'catalog.manage')` faqat doira BO'SH emasligini
   * tekshiradi, uni satrlarga solishtirmaydi.
   */
  const canManage = canAnywhere('catalog.manage');

  const nomenclatures = useNomenclatures({
    ...(search === '' ? {} : { search }),
    ...(categoryId === '' ? {} : { categoryId }),
  });
  const categories = useCategories();

  const categoryNames = useMemo(
    () => new Map((categories.data?.items ?? []).map((category) => [category.id, category.name])),
    [categories.data],
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: catalogKeys.all });
  };

  const create = useMutation({
    mutationFn: (values: NomenclatureFormValues) =>
      catalogApi.create({
        code: values.code,
        name: values.name,
        unit: values.unit,
        categoryId: categoryIdOf(values.categoryId),
        minStock: values.minStock,
      }),
    onSuccess: () => {
      setDialogOpen(false);
      toast.show(t('catalog.created'), 'success');
      invalidate();
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiClientError ? error.message : t('catalog.saveFailed'));
    },
  });

  /* Kod ham, birlik ham yuborilmaydi — `updateNomenclatureSchema` ularni
     qabul qilmaydi (birlik o'zgarsa mavjud qoldiq noto'g'ri o'qiladi). */
  const update = useMutation({
    mutationFn: (input: { id: string; values: NomenclatureFormValues }) =>
      catalogApi.update(input.id, {
        name: input.values.name,
        categoryId: categoryIdOf(input.values.categoryId),
        minStock: input.values.minStock,
        isActive: input.values.isActive,
      }),
    onSuccess: () => {
      setDialogOpen(false);
      toast.show(t('catalog.updated'), 'success');
      invalidate();
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiClientError ? error.message : t('catalog.saveFailed'));
    },
  });

  /**
   * O'chirish — faqat hech qayerda ishlatilmagan mahsulot uchun.
   *
   * Ishlatilgan bo'lsa backend 409 va NIMA QILISH kerakligini qaytaradi;
   * o'sha matn oynada qoladi va yonida "Faolsizlantirish" tugmasi paydo
   * bo'ladi — odam oynani yopib, boshqa yo'lni o'zi qidirmaydi.
   */
  const remove = useMutation({
    mutationFn: (row: NomenclatureDto) => catalogApi.remove(row.id),
    onSuccess: () => {
      setDeleting(null);
      toast.show(t('catalog.deleted'), 'success');
      invalidate();
    },
    onError: (error: unknown) => {
      setDeleteError(error instanceof ApiClientError ? error.message : t('catalog.deleteFailed'));
    },
  });

  const deactivate = useMutation({
    mutationFn: (row: NomenclatureDto) => catalogApi.update(row.id, { isActive: false }),
    onSuccess: () => {
      setDeleting(null);
      toast.show(t('catalog.deactivated'), 'success');
      invalidate();
    },
    onError: (error: unknown) => {
      setDeleteError(error instanceof ApiClientError ? error.message : t('catalog.saveFailed'));
    },
  });

  const openDelete = (row: NomenclatureDto): void => {
    setDeleting(row);
    setDeleteError(null);
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: NomenclatureDto): void => {
    if (!canManage) return;
    setEditing(row);
    setFormError(null);
    setDialogOpen(true);
  };

  const columns: Column<NomenclatureDto>[] = [
    { key: 'code', header: t('catalog.colCode'), mono: true, width: '11rem' },
    {
      key: 'name',
      header: t('catalog.colName'),
      render: (row) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate">{row.name}</span>
          {row.isActive ? null : <Badge tone="neutral">{t('catalog.archived')}</Badge>}
        </span>
      ),
    },
    {
      key: 'unit',
      header: t('catalog.colUnit'),
      width: '7rem',
      render: (row) => unitText(row.unit),
    },
    {
      key: 'categoryId',
      header: t('catalog.colCategory'),
      width: '14rem',
      render: (row) =>
        row.categoryId === null
          ? t('catalog.categoryNone')
          : (categoryNames.get(row.categoryId) ?? row.categoryId),
    },
    {
      key: 'minStock',
      header: t('catalog.colMinStock'),
      align: 'right',
      mono: true,
      width: '9rem',
      render: (row) => `${row.minStock} ${unitText(row.unit)}`,
    },
    /*
     * AMALLAR USTUNI — tahrir qatorni bosish bilan ham ochiladi, lekin
     * bosiladigan qator ekranda hech qanday belgi bermaydi: odam uni topishi
     * uchun tugma KO'RINIB turishi kerak. O'chirish esa qatorni bosish bilan
     * umuman ochilmaydi va bu ataylab.
     */
    ...(canManage
      ? [
          {
            key: 'actions' as const,
            header: '',
            align: 'right' as const,
            width: '7rem',
            render: (row: NomenclatureDto) => (
              <span
                className="flex items-center justify-end gap-1"
                // Qator bosilishi tahrirni ochadi — tugmalar uni takrorlamasin.
                onClick={(event) => event.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('catalog.editFor', { name: row.name })}
                  onClick={() => openEdit(row)}
                >
                  <Pencil size={15} aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('catalog.deleteFor', { name: row.name })}
                  onClick={() => openDelete(row)}
                >
                  <Trash2 size={15} aria-hidden />
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  const items = nomenclatures.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('catalog.title')}
        description={t('catalog.subtitle')}
        filters={
          <>
            <Input
              variant="filter"
              label={t('catalog.searchLabel')}
              placeholder={t('catalog.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              variant="filter"
              label={t('catalog.filterCategory')}
              placeholder={t('catalog.filterAll')}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              options={(categories.data?.items ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
          </>
        }
        actions={
          canManage ? (
            <Button onClick={openCreate}>
              <Plus size={16} aria-hidden />
              {t('catalog.newNomenclature')}
            </Button>
          ) : undefined
        }
      />

      {nomenclatures.isPending ? (
        <Skeleton lines={8} className="w-full" />
      ) : nomenclatures.isError ? (
        /*
         * Xato turi bo'yicha AYRIM xabar: tarmoq uzilishi, 403, 404 va
         * 500 bir xil emas va odamga har birida boshqa narsa qilish
         * kerak. Texnik tafsilot ekranga chiqmaydi.
         *
         * Ro'yxat, filtr va qidiruv bu shoxda umuman chizilmaydi —
         * ular ishlamaydigan ma'lumot ustida bo'sh va'da bo'lardi.
         */
        <ApiErrorState error={nomenclatures.error} onRetry={() => void nomenclatures.refetch()} />
      ) : (
        <>
          <ResponsiveTable
            caption={t('catalog.tableCaption')}
            /* Telefonda kartochka: kod tepada kichik, NOM esa bosh matn.
               Qolgan ustunlar «yorliq: qiymat» qatorlariga O'ZI tushadi —
               ro'yxat qo'lda yozilmaydi, shuning uchun keyin qo'shilgan
               ustun ham kartochkada paydo bo'ladi. */
            card={{
              code: 'code',
              title: 'name',
              actions: 'actions',
              numbered: true,
              /* Birlik va chegara — bir qarashda o'qiladigan qisqa
                 qiymatlar: ular ro'yxatda ko'z yuguradigan joy bo'ladi. */
              chips: ['unit', 'minStock'],
              /* Turkum nomi uzun bo'lishi mumkin («Yuqori tuzilma
                 elementlari») — tor ustunda so'zma-so'z sinib ketardi. */
              wide: ['categoryId'],
              /*
               * HOLAT ustun emas: jadvalda u nom yonidagi belgi bilan
               * aytiladi va alohida ustunga joy yo'q. Kartochkada esa
               * cheklov yo'q, shuning uchun u o'z yorlig'i bilan chiqadi
               * — `NomenclatureDto` dagi maydon tushib qolmasin.
               */
              extra: [
                {
                  key: 'isActive',
                  label: t('catalog.colStatus'),
                  chip: true,
                  render: (row) => (row.isActive ? t('catalog.active') : t('catalog.archived')),
                },
              ],
              /* Jadval katagida nom `truncate` bilan bir qatorga kesiladi —
                 u yerda qator balandligi qat'iy. Kartochkada esa joy bor
                 va nom ikki qatorga o'ralib, to'liq o'qiladi. Belgi bu
                 yerda takrorlanmaydi: holat pastda o'z maydonida turadi. */
              renderTitle: (row) => row.name,
            }}
            density="compact"
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            onRowClick={canManage ? openEdit : undefined}
            empty={
              <EmptyState
                icon={<Boxes size={20} aria-hidden />}
                title={t('catalog.listEmpty')}
                description={t('catalog.listEmptyHint')}
              />
            }
          />
          <p className="tabular text-xs text-text-faint">
            {t('catalog.total', { count: nomenclatures.data?.total ?? items.length })}
          </p>
        </>
      )}

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        size="sm"
        title={t('catalog.deleteTitle')}
        /* Xato endi oynaning O'Z blokida — ilgari u tanada qo'lda yasalgan
           qizil qutcha edi va har modalda boshqacha ko'rinardi. */
        error={deleteError}
        busy={remove.isPending || deactivate.isPending}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              {t('catalog.cancel')}
            </Button>
            {/* Faolsizlantirish faqat o'chirish RAD ETILGANDAN keyin chiqadi:
                ikkala tugma birdan turgan oyna tanlovni qiyinlashtiradi. */}
            {deleteError === null ? (
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => {
                  if (deleting !== null) remove.mutate(deleting);
                }}
              >
                {t('catalog.delete')}
              </Button>
            ) : (
              <Button
                loading={deactivate.isPending}
                onClick={() => {
                  if (deleting !== null) deactivate.mutate(deleting);
                }}
              >
                {t('catalog.deactivate')}
              </Button>
            )}
          </>
        }
      >
        {deleting === null ? null : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="tabular text-text-faint text-sm">{deleting.code}</span>
              <span className="text-text text-sm font-medium">{deleting.name}</span>
            </div>

            {deleteError === null ? (
              <p className="text-text-muted text-sm text-pretty">{t('catalog.deleteWarning')}</p>
            ) : null}
          </div>
        )}
      </Dialog>

      {canManage ? (
        <NomenclatureDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          nomenclature={editing}
          categories={categories.data?.items ?? []}
          pending={create.isPending || update.isPending}
          error={formError}
          onSubmit={(values) => {
            setFormError(null);
            if (editing === null) create.mutate(values);
            else update.mutate({ id: editing.id, values });
          }}
        />
      ) : null}
    </div>
  );
};
